import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Loader2,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  RotateCcw,
  Hash,
  UserCheck,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { openWhatsApp, TEMPLATES, formatAppointmentDateTime } from "../lib/whatsapp";

const statusColors = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  no_show: "bg-amber-50 text-amber-700 border-amber-200",
};

function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function endOfDay(d) { const x = new Date(d); x.setHours(23,59,59,999); return x; }
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}
function dayLabel(d) {
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

export default function Appointments() {
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      let start, end;
      if (view === "day") {
        start = startOfDay(anchor);
        end = endOfDay(anchor);
      } else {
        const day = anchor.getDay();
        const weekStart = new Date(anchor);
        weekStart.setDate(anchor.getDate() - day);
        start = startOfDay(weekStart);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        end = endOfDay(weekEnd);
      }
      const params = { start: start.toISOString(), end: end.toISOString() };
      if (doctorFilter !== "all") params.doctor_id = doctorFilter;
      const { data } = await api.get("/appointments", { params });
      setAppointments(data);
    } catch (e) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const { data } = await api.get("/patients", { params: { limit: 200 } });
      setPatients(data.items || data);
    } catch (e) { console.error(e); }
  };

  const loadDoctors = async () => {
    try {
      const { data } = await api.get("/doctors");
      setDoctors(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor, view, doctorFilter]);
  useEffect(() => { loadPatients(); loadDoctors(); }, []);

  // Fetch clinic name for WhatsApp templates
  const [clinicName, setClinicName] = useState("our clinic");
  useEffect(() => {
    api.get("/clinic/settings").then(({ data }) => setClinicName(data?.name || "our clinic")).catch(() => {});
  }, []);

  // Open dialogs via FAB / navigation state
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.action === "book") setBookOpen(true);
      if (e.detail?.action === "walkin") setWalkInOpen(true);
    };
    window.addEventListener("hedesk:fab", handler);
    return () => window.removeEventListener("hedesk:fab", handler);
  }, []);

  // Honor navigation state from cross-page FAB triggers
  const _loc = useLocation();
  useEffect(() => {
    if (_loc.state?.openBook) setBookOpen(true);
    if (_loc.state?.openWalkIn) setWalkInOpen(true);
    if (_loc.state) {
      // Clear so refresh doesn't reopen
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const deleteAppointment = async (id) => {
    if (!confirm("Delete this appointment?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const sendWhatsApp = (a) => {
    if (!a.patient_phone) {
      toast.error("Patient phone is missing");
      return;
    }
    const fmt = formatAppointmentDateTime(a.date_time);
    const msg = TEMPLATES.appointmentReminder({
      patient_name: a.patient_name,
      clinic_name: clinicName,
      appointment_date: fmt.date,
      appointment_time: fmt.time,
    });
    openWhatsApp(a.patient_phone, msg);
  };

  const checkIn = async (id) => {
    try {
      await api.put(`/appointments/${id}/check-in`);
      toast.success("Checked in — added to queue");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to check in");
    }
  };

  const navigate = (dir) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + (view === "day" ? dir : dir * 7));
    setAnchor(d);
  };

  const groupedByDay = appointments.reduce((acc, a) => {
    const k = a.date_time.slice(0, 10);
    (acc[k] = acc[k] || []).push(a);
    return acc;
  }, {});

  const days = view === "week"
    ? Array.from({ length: 7 }).map((_, i) => {
        const day = anchor.getDay();
        const start = new Date(anchor);
        start.setDate(anchor.getDate() - day + i);
        return start;
      })
    : [anchor];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-in" data-testid="appointments-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">Schedule</div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Appointments
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setWalkInOpen(true)} variant="outline" data-testid="walk-in-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Walk-in
          </Button>
          <Button onClick={() => setBookOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-appointment-btn">
            <Plus className="w-4 h-4 mr-2" /> New appointment
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => setAnchor(new Date())} data-testid="today-btn">Today</Button>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} data-testid="next-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="ml-3 text-sm font-medium text-slate-700">
            {view === "day"
              ? anchor.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
              : `Week of ${days[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {doctors.length > 1 && (
            <Select value={doctorFilter} onValueChange={setDoctorFilter}>
              <SelectTrigger className="w-48 h-9" data-testid="doctor-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All doctors</SelectItem>
                {doctors.map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="inline-flex rounded-lg bg-slate-100 p-1">
            <button
              onClick={() => setView("day")}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === "day" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}
              data-testid="view-day-btn"
            >Day</button>
            <button
              onClick={() => setView("week")}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === "week" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}
              data-testid="view-week-btn"
            >Week</button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className={`grid gap-4 ${view === "week" ? "grid-cols-1 lg:grid-cols-7" : "grid-cols-1"}`}>
          {days.map((d) => {
            const key = fmtDate(d);
            const list = groupedByDay[key] || [];
            return (
              <div key={key} className="space-y-3">
                {view === "week" && (
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{dayLabel(d)}</div>
                )}
                {list.length === 0 ? (
                  <Card className="border-dashed border-slate-300 bg-slate-50/40">
                    <CardContent className="p-6 text-center text-slate-400 text-sm">No appointments</CardContent>
                  </Card>
                ) : list.map((a) => (
                  <Card key={a.appointment_id} className="border-slate-200" data-testid={`appt-card-${a.appointment_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900">{fmtTime(a.date_time)}</span>
                            <Badge variant="outline" className={statusColors[a.status]}>
                              {a.status.replace("_", " ")}
                            </Badge>
                            {a.type === "walk_in" && (
                              <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700 gap-1" data-testid={`walkin-badge-${a.appointment_id}`}>
                                <Hash className="w-3 h-3" /> #{a.queue_number} walk-in
                              </Badge>
                            )}
                            {a.rescheduled_from && (
                              <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-500 text-xs">rescheduled</Badge>
                            )}
                          </div>
                          <div className="mt-1 font-medium text-slate-900">{a.patient_name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{a.reason || "Consultation"} · {a.doctor_name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{a.patient_phone}</div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => deleteAppointment(a.appointment_id)} data-testid={`delete-appt-${a.appointment_id}`}>
                          <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Button size="sm" variant="outline" onClick={() => updateStatus(a.appointment_id, "completed")} className="h-7 text-xs" data-testid={`status-completed-${a.appointment_id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(a.appointment_id, "cancelled")} className="h-7 text-xs" data-testid={`status-cancelled-${a.appointment_id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(a.appointment_id, "no_show")} className="h-7 text-xs" data-testid={`status-noshow-${a.appointment_id}`}>
                          <AlertCircle className="w-3 h-3 mr-1" /> No-show
                        </Button>
                        {a.type !== "walk_in" && a.status === "scheduled" && !a.queue_status && (
                          <Button size="sm" variant="outline" onClick={() => checkIn(a.appointment_id)} className="h-7 text-xs" data-testid={`checkin-${a.appointment_id}`}>
                            <UserCheck className="w-3 h-3 mr-1" /> Check in
                          </Button>
                        )}
                        {a.queue_status && (
                          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 text-xs" data-testid={`queued-${a.appointment_id}`}>
                            In queue · {a.queue_status.replace("_", " ")}
                          </Badge>
                        )}
                        {a.type !== "walk_in" && a.status === "scheduled" && (
                          <Button size="sm" variant="outline" onClick={() => setRescheduleTarget(a)} className="h-7 text-xs" data-testid={`reschedule-${a.appointment_id}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Reschedule
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => sendWhatsApp(a)} className="h-7 text-xs text-green-700 border-green-200 hover:bg-green-50 hover:text-green-800" data-testid={`whatsapp-${a.appointment_id}`}>
                          <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          })}
        </div>
      )}

      <BookAppointmentDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        patients={patients}
        doctors={doctors}
        onCreated={() => load()}
      />
      <WalkInDialog
        open={walkInOpen}
        onOpenChange={setWalkInOpen}
        patients={patients}
        doctors={doctors}
        onCreated={() => load()}
      />
      <RescheduleDialog
        target={rescheduleTarget}
        doctors={doctors}
        onClose={() => setRescheduleTarget(null)}
        onSaved={() => { setRescheduleTarget(null); load(); }}
      />
    </div>
  );
}

function SlotGrid({ doctorId, date, durationMinutes, value, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!doctorId || !date) return;
    setLoading(true);
    api.get("/slots", { params: { doctor_id: doctorId, date } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [doctorId, date]);

  if (loading) return <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-blue-600 mx-auto" /></div>;
  if (!data) return null;
  if (data.on_leave) return <div className="p-4 text-center text-sm text-amber-700 bg-amber-50 rounded-lg border border-amber-200">Doctor is on leave this day.</div>;
  if (data.slots.length === 0) return <div className="p-4 text-center text-sm text-slate-500 bg-slate-50 rounded-lg border border-slate-200">No working hours configured for this day.</div>;

  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-72 overflow-auto p-1" data-testid="slot-grid">
      {data.slots.map((s) => {
        const selected = value === s.time;
        const base = "px-2 py-2 text-sm rounded-md border transition-all text-center";
        if (!s.available) {
          return (
            <button key={s.time} type="button" disabled
              className={`${base} bg-slate-100 border-slate-200 text-slate-400 line-through cursor-not-allowed`}
              data-testid={`slot-${s.time}`}
              title={s.patient_name ? `Booked: ${s.patient_name}` : "Booked"}
            >{s.time}</button>
          );
        }
        return (
          <button key={s.time} type="button"
            onClick={() => onChange(s.time)}
            className={`${base} ${selected ? "bg-blue-600 border-blue-600 text-white font-semibold" : "bg-white border-slate-200 hover:border-blue-500 text-slate-700"}`}
            data-testid={`slot-${s.time}`}
          >{s.time}</button>
        );
      })}
    </div>
  );
}

function BookAppointmentDialog({ open, onOpenChange, patients, doctors, onCreated }) {
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", date: "", time: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      const t = new Date();
      setForm({
        patient_id: patients[0]?.patient_id || "",
        doctor_id: doctors[0]?.user_id || "",
        date: t.toISOString().slice(0, 10),
        time: "",
        reason: "",
      });
      setError("");
    }
  }, [open, patients, doctors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.time) { setError("Pick a time slot"); return; }
    setSubmitting(true);
    try {
      const dt = new Date(`${form.date}T${form.time}:00`);
      await api.post("/appointments", {
        patient_id: form.patient_id,
        doctor_id: form.doctor_id,
        date_time: dt.toISOString(),
        duration_minutes: 30,
        reason: form.reason,
      });
      toast.success("Appointment booked");
      onOpenChange(false);
      onCreated();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="book-appointment-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Book new appointment</DialogTitle>
          <DialogDescription>Pick doctor and date, then choose an available slot.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Doctor</Label>
              <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v, time: "" })}>
                <SelectTrigger data-testid="book-doctor-select" className="mt-1.5"><SelectValue placeholder="Doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (<SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="apt-date">Date</Label>
              <Input id="apt-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, time: "" })} required data-testid="appt-date-input" className="mt-1.5 h-10" />
            </div>
          </div>
          <div>
            <Label>Patient</Label>
            <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
              <SelectTrigger data-testid="appt-patient-select" className="mt-1.5"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map((p) => (<SelectItem key={p.patient_id} value={p.patient_id}>{p.name} · {p.phone}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Available slot</Label>
            <div className="mt-1.5">
              <SlotGrid
                doctorId={form.doctor_id}
                date={form.date}
                value={form.time}
                onChange={(t) => setForm({ ...form, time: t })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="apt-reason">Reason (optional)</Label>
            <Input id="apt-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. General checkup" data-testid="appt-reason-input" className="mt-1.5 h-10" />
          </div>
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="appt-error">{error}</div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting || !form.patient_id || !form.doctor_id || !form.time} className="bg-blue-600 hover:bg-blue-700" data-testid="appt-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book appointment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function WalkInDialog({ open, onOpenChange, patients, doctors, onCreated }) {
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        patient_id: patients[0]?.patient_id || "",
        doctor_id: doctors[0]?.user_id || "",
        reason: "",
      });
      setError("");
    }
  }, [open, patients, doctors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments/walk-in", {
        patient_id: form.patient_id,
        doctor_id: form.doctor_id,
        reason: form.reason,
      });
      toast.success(`Walk-in added · Queue #${data.queue_number}`);
      onOpenChange(false);
      onCreated();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="walkin-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }} className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" /> Add walk-in patient
          </DialogTitle>
          <DialogDescription>The patient will be added to today's queue. No slot required.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label>Patient</Label>
            <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
              <SelectTrigger data-testid="walkin-patient-select" className="mt-1.5"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map((p) => (<SelectItem key={p.patient_id} value={p.patient_id}>{p.name} · {p.phone}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Doctor</Label>
            <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v })}>
              <SelectTrigger data-testid="walkin-doctor-select" className="mt-1.5"><SelectValue placeholder="Doctor" /></SelectTrigger>
              <SelectContent>
                {doctors.map((d) => (<SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 h-10" data-testid="walkin-reason" />
          </div>
          {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="walkin-error">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting || !form.patient_id || !form.doctor_id} className="bg-blue-600 hover:bg-blue-700" data-testid="walkin-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add to queue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleDialog({ target, doctors, onClose, onSaved }) {
  const [form, setForm] = useState({ doctor_id: "", date: "", time: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (target) {
      setForm({
        doctor_id: target.doctor_id,
        date: target.date_time.slice(0, 10),
        time: "",
      });
      setError("");
    }
  }, [target]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.time) { setError("Pick a new slot"); return; }
    setSubmitting(true);
    try {
      const dt = new Date(`${form.date}T${form.time}:00`);
      await api.put(`/appointments/${target.appointment_id}/reschedule`, {
        date_time: dt.toISOString(),
        doctor_id: form.doctor_id,
      });
      toast.success("Rescheduled");
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="reschedule-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }} className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-blue-600" /> Reschedule
          </DialogTitle>
          <DialogDescription>
            {target && (<>Move {target.patient_name}'s appointment from {fmtTime(target.date_time)} on {target.date_time.slice(0, 10)} to a new slot.</>)}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Doctor</Label>
              <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v, time: "" })}>
                <SelectTrigger data-testid="reschedule-doctor-select" className="mt-1.5"><SelectValue placeholder="Doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (<SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>New date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, time: "" })} required data-testid="reschedule-date-input" className="mt-1.5 h-10" />
            </div>
          </div>
          <div>
            <Label>New slot</Label>
            <div className="mt-1.5">
              <SlotGrid
                doctorId={form.doctor_id}
                date={form.date}
                value={form.time}
                onChange={(t) => setForm({ ...form, time: t })}
              />
            </div>
          </div>
          {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reschedule-error">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting || !form.time} className="bg-blue-600 hover:bg-blue-700" data-testid="reschedule-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reschedule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
